import {
  Component,
  inject,
  input,
  signal,
  OnInit,
  OnDestroy,
  computed,
} from '@angular/core'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { Filter, FilterService } from '../../shared/services/filter/filter.service'
import { FieldColumnsComponent } from '../field-columns/field-columns.component'
import {
  DashboardConfig,
} from '../../shared/models/dashboardConfig'
import { Subscription } from 'rxjs'
import {
  EntityRef,
  ServicePointIdReference,
} from '../../shared/models/referenceData'


import { StringFilter } from '../../shared/services/filter/string-filter'
import { ServicePointGroupFilter } from '../../shared/services/filter/service-point-group-filter'
import { ServicePointFilter } from '../../shared/services/filter/service-point-filter'
import { EntityFilter } from '../../shared/services/filter/entity-filter'
import { FlagFilter } from '../../shared/services/filter/flag-filter'
import { DateRangeFilter, DateRangeFilterStateValue } from '../../shared/services/filter/date-range-filter'
import { DateRangeFilterPickerComponent } from '../date-range-filter-picker/date-range-filter-picker.component'
import { EntitySelectorComponent } from '../entity-selector/entity-selector.component'
import { ServicePointSelectorComponent } from '../service-point-selector/service-point-selector.component'
import {
  ServicePointGroupSelectorComponent,
} from '../service-point-group-selector/service-point-group-selector.component'
import { BoolDisplayPipe } from '../../shared/pipes/boolDisplay.pipe'
import { SelectorComponent } from '../selector/selector.component'
import { SelectMenuSearchComponent } from '../table/filters/select-menu-search/select-menu-search.component'
import { NumberRangeSelector } from '../number-range-selector/number-range-selector.component'
import { NumberRangeFilter } from '../../shared/services/filter/number-range-filter'
import { Option } from '../../shared/models/general'
import { ServicePointGroupWithRefs } from '../service-point-groups/service-point-group.types'

@Component({
  imports: [
    FieldColumnsComponent,
    LabeledPropertyComponent,
    DateRangeFilterPickerComponent,
    SelectorComponent,
    EntitySelectorComponent,
    ServicePointSelectorComponent,
    ServicePointGroupSelectorComponent,
    SelectMenuSearchComponent,
    NumberRangeSelector,
  ],
  selector: 'app-dashboard-filter',
  styles: `
    .coming-soon {
      display: block;
      padding: 1rem 1.75rem .25rem .25rem;
      margin: 0;
      width: 100%;
    }
  `,
  template: `
    <div app-field-columns>
      @for (f of filters(); track f.state.config.meta.fieldName) {
        <div app-labeled-property [label]="f.state.config.meta.fieldDisplay">
          @switch (f.type) { 
            @case ('DATE_RANGE') {
              <div dateRangeFilterPicker
                [value]="f.state"
                (valueChange)="handleDateRangeSelect($event, f.state.config.meta.fieldName)"
                [showOperation]="f.state.config.showOperation ?? false"
                [showRange]="f.state.config.showRange ?? false"
                [showTime]="f.state.config.showTime ?? false"
                [showSeconds]="f.state.config.showSeconds ?? false"
                [onlyMonth]="f.state.config.onlyMonth ?? false"
              >
              </div>
            }
  
            @case ('ENTITY') {
              <div
                app-entity-selector
                (valueChange)="handleEntitySelect($event, f.state.config.meta.fieldName)"
                (wildcardValueChange)="handleEntityWildcardSelect($event, f.state.config.meta.fieldName)"
                [selectedEntities]="f.state.entities"
                [selectedWildcards]="f.state.wildcards"
                [config]="f.state.config"
                [slim]="true"
              >
              </div>
            }
  
            @case ('FLAG') {            
              <div
                app-selector
                [selected]="toFlagOption(f.state.value)"
                [options]="flagOptions"
                [slim]="true"
                (selectedChange)="handleFlagSelect($event, f.state.config.meta.fieldName)"
              >
              </div>
            }
  
            @case ('NUMBER_RANGE'){
              <div 
                app-number-range-selector
                [value]="{min: f.state.min, max: f.state.max}"
                (valueChange)="handleNumberRangeSelect($event, f.state.config.meta.fieldName)"
                [allowRange]="f.state.config.allowRange"
                [precision]="f.state.config.precision"
              >
              </div>
            }
  
  
            @case ('SERVICE_POINT') {            
              <div
                app-service-point-selector
                (valueChange)="handleServicePointSelect($event, f.state.config.meta.fieldName)"
                [selectedServicePoints]="f.state.servicePoints"
                [slim]="true"
                [config]="f.state.config"
                [allowPartial]="false"
              >
              </div>
            }
            
            @case ('SERVICE_POINT_GROUP') {
              <div
                app-service-point-group-selector
                (valueChange)="handleServicePointGroupSelect($event, f.state.config.meta.fieldName)"
                [config]="f.state.config"
                [selectedGroups]="f.state.servicePointGroups"
                [slim]="true"
              >
              </div>
            }
  
            @case ('STRING') {
              <div
                app-select-menu-search
                [name]="f.state.config.meta.fieldName"
                [value]="stringSearch()[f.state.config.meta.fieldName]"
                [showAdd]="f.state.values.length < 1 || f.state.config.allowMultiple"
                [showSearch]="false"
                (search)="setStringSearch(f.state.config.meta.fieldName, $event)"
                (add)="handleStringSelect($event, f.state.config.meta.fieldName)"
              >
              </div>
            }
  
            @default {
              <span class="coming-soon">Coming Soon</span>
            }
          }
        </div>
      }
    </div>
  `,
})
export class DashboardFilter implements OnInit, OnDestroy {
  private readonly filterService = inject(FilterService)
  private readonly subscription = new Subscription()

  readonly dashboard = input.required<DashboardConfig>()

  protected readonly filters = signal<readonly Filter[]>([])
  protected readonly stringSearch = signal<Readonly<Record<string, string>>>({})
  
  protected readonly fieldGroup = computed((): string => this.dashboard().name)

  protected readonly flagOptions: readonly Option<boolean | null>[] = [
    { value: null, display: 'Either' },
    { value: true, display: new BoolDisplayPipe().transform(true) },
    { value: false, display: new BoolDisplayPipe().transform(false) },
  ]

  ngOnInit() {
    this.subscription.add(
      this.filterService
        .state(this.fieldGroup())
        .subscribe(state => {
          const filters = this.dashboard().params
            .filter(_ => !_.constant)
            .map(_ => state[_.widget])
            .filter(Boolean)
          const stringSearch: Record<string, string> = {}
          filters.filter(_ => _.type === 'STRING').forEach(f => {
            stringSearch[f.state.config.meta.fieldName] = f.state.values[f.state.values.length - 1] ?? ''
          })

          this.filters.set(filters)
          this.stringSearch.set({ ...this.stringSearch(), ...stringSearch })
        }),
    )
  }

  protected setStringSearch(key: string, value: string) {
    this.stringSearch.set({ ...this.stringSearch(), [key]: value })
  }

  protected toFlagOption(b: boolean | null): Option<boolean | null> {
    switch (b) {
      case true: return this.flagOptions[1]
      case false: return this.flagOptions[2]
      default: return this.flagOptions[0]
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  protected handleEntitySelect(evt: EntityRef, field: string) {
    this.filterService.dispatch(
      EntityFilter.AddEntityRefsAction(
        { entities: [evt] },
        { fieldGroup: this.fieldGroup(), fieldName: field },
      ),
    )
  }

  protected handleEntityWildcardSelect(evt: string, field: string) {
    this.filterService.dispatch(
      EntityFilter.AddEntityWildcardsAction(
        { wildcards: [evt] },
        { fieldGroup: this.fieldGroup(), fieldName: field },
      ),
    )
  }

  protected handleServicePointSelect(evt: ServicePointIdReference, field: string) {
    this.filterService.dispatch(
      ServicePointFilter.AddServicePointsAction(
        { servicePoints: [evt] },
        { fieldGroup: this.fieldGroup(), fieldName: field },
      ),
    )
  }

  protected handleServicePointGroupSelect(
    evt: ServicePointGroupWithRefs,
    field: string,
  ) {
    this.filterService.dispatch(
      ServicePointGroupFilter.AddServicePointGroupsAction(
        { servicePointGroups: [evt] },
        { fieldGroup: this.fieldGroup(), fieldName: field },
      ),
    )
  }

  protected handleStringSelect(evt: string, field: string) {
    this.filterService.dispatch(
      StringFilter.AddStringsAction(
        { values: [evt] },
        { fieldGroup: this.fieldGroup(), fieldName: field },
      ),
    )
  }

  protected handleFlagSelect(evt: Option<boolean | null>, field: string) {
    this.filterService.dispatch(
      FlagFilter.SetFlagFilterValueAction(
        { value: evt.value },
        { fieldGroup: this.fieldGroup(), fieldName: field },
      ),
    )
  }

  protected handleNumberRangeSelect(evt: { min: number, max: number }, field: string) {
    this.filterService.dispatch(
      NumberRangeFilter.SetNumberRangeFilterValueAction(
        { min: evt.min, max: evt.max },
        { fieldGroup: this.fieldGroup(), fieldName: field },
      ),
    )
  }

  protected handleDateRangeSelect(evt: DateRangeFilterStateValue, field: string) {
    this.filterService.dispatch(
      DateRangeFilter.SetDateRangeFilterValueAction(evt, { fieldGroup: this.fieldGroup(), fieldName: field }),
    )
  }
}
